/**
 * AudioTimeline.jsx — scrubbable waveform player for Studio renders.
 *
 * Replaces the bare "Play" button: real peaks drawn from the decoded audio,
 * a draggable playhead, keyboard nudges, and an optional looping ambience bed
 * layered underneath at the project's chosen level.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import * as L from "lucide-react";
import api from "@/lib/api";

const PEAKS = 220;

function b64ToBlobUrl(b64, mime) {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

export function AudioTimeline({
  audioB64,
  mime = "audio/mpeg",
  ambienceB64 = null,
  ambienceMime = "audio/mpeg",
  ambienceVolume = 0,
  musicUrl = null,
  musicVolume = 0,
  musicDuck = 0.7,
  duckEnvelope = null,     // Float32Array at ENV_RATE, from studio-mix
  envRate = 100,
  chapters = [],
  durationHint = 0,
}) {
  const [peaks, setPeaks] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(durationHint || 0);
  const audioRef = useRef(null);
  const ambRef = useRef(null);
  const canvasRef = useRef(null);
  const musicRef = useRef(null);
  const musicCtxRef = useRef(null);
  const musicGainRef = useRef(null);
  const [musicSrc, setMusicSrc] = useState(null);

  const url = useMemo(() => (audioB64 ? b64ToBlobUrl(audioB64, mime) : null), [audioB64, mime]);
  const ambUrl = useMemo(
    () => (ambienceB64 && ambienceVolume > 0 ? b64ToBlobUrl(ambienceB64, ambienceMime) : null),
    [ambienceB64, ambienceMime, ambienceVolume]
  );

  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
    if (ambUrl) URL.revokeObjectURL(ambUrl);
  }, [url, ambUrl]);

  // Music bed lives behind an authenticated URL — fetch it once as a blob.
  useEffect(() => {
    let objUrl = null;
    let dead = false;
    if (musicUrl && musicVolume > 0) {
      const path = musicUrl.replace(/^\/api/, "");
      api.get(path, { responseType: "blob", timeout: 180000 })
        .then(res => {
          if (dead) return;
          objUrl = URL.createObjectURL(res.data);
          setMusicSrc(objUrl);
        })
        .catch(() => {});
    } else {
      setMusicSrc(null);
    }
    return () => {
      dead = true;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [musicUrl, musicVolume]);

  // Wire the music element through a gain node so it can duck under the voice
  // using the same envelope the export uses.
  const startMusic = async (fromSeconds) => {
    const el = musicRef.current;
    if (!el) return;
    try {
      if (!musicCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const srcNode = ctx.createMediaElementSource(el);
        const gain = ctx.createGain();
        srcNode.connect(gain);
        gain.connect(ctx.destination);
        musicCtxRef.current = ctx;
        musicGainRef.current = gain;
      }
      const ctx = musicCtxRef.current;
      const gain = musicGainRef.current;
      await ctx.resume().catch(() => {});
      gain.gain.cancelScheduledValues(ctx.currentTime);
      if (duckEnvelope && duckEnvelope.length) {
        const startIdx = Math.min(duckEnvelope.length - 1, Math.max(0, Math.floor(fromSeconds * envRate)));
        const slice = duckEnvelope.slice(startIdx);
        gain.gain.setValueAtTime(slice[0] ?? musicVolume, ctx.currentTime);
        gain.gain.setValueCurveAtTime(slice, ctx.currentTime, Math.max(0.2, slice.length / envRate));
      } else {
        gain.gain.setValueAtTime(musicVolume * (1 - musicDuck), ctx.currentTime);
      }
      el.currentTime = 0;
      await el.play();
    } catch (_) { /* music is a nicety, never a blocker */ }
  };

  const stopMusic = () => {
    try { musicRef.current?.pause(); } catch (_) { /* ignore */ }
    const ctx = musicCtxRef.current;
    if (ctx && musicGainRef.current) {
      try { musicGainRef.current.gain.cancelScheduledValues(ctx.currentTime); } catch (_) { /* ignore */ }
    }
  };

  useEffect(() => () => {
    stopMusic();
    try { musicCtxRef.current?.close(); } catch (_) { /* ignore */ }
  }, []);

  // Decode once for the waveform peaks.
  useEffect(() => {
    let dead = false;
    if (!audioB64) return;
    (async () => {
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        const ctx = new AC();
        const bytes = Uint8Array.from(atob(audioB64), c => c.charCodeAt(0));
        const buf = await ctx.decodeAudioData(bytes.buffer.slice(0));
        const data = buf.getChannelData(0);
        const block = Math.floor(data.length / PEAKS) || 1;
        const out = new Float32Array(PEAKS);
        for (let i = 0; i < PEAKS; i++) {
          let max = 0;
          for (let j = 0; j < block; j += 8) {
            const v = Math.abs(data[i * block + j] || 0);
            if (v > max) max = v;
          }
          out[i] = max;
        }
        await ctx.close().catch(() => {});
        if (!dead) {
          setPeaks(out);
          setDuration(buf.duration);
        }
      } catch (e) {
        console.warn("waveform decode failed", e);
      }
    })();
    return () => { dead = true; };
  }, [audioB64]);

  // Paint waveform + progress.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    const n = peaks?.length || PEAKS;
    const step = w / n;
    const pct = duration ? Math.min(1, time / duration) : 0;
    for (let i = 0; i < n; i++) {
      const v = peaks ? peaks[i] : 0.12;
      const barH = Math.max(2, v * h * 0.92);
      const x = i * step;
      ctx.fillStyle = (i / n) <= pct ? "#E89A3C" : "rgba(255,255,255,0.28)";
      ctx.fillRect(x, (h - barH) / 2, Math.max(1, step * 0.6), barH);
    }
  }, [peaks, time, duration]);

  const seekTo = (fraction) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    el.currentTime = Math.max(0, Math.min(duration - 0.05, fraction * duration));
    setTime(el.currentTime);
  };

  const onCanvasClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    seekTo((e.clientX - rect.left) / rect.width);
  };

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      ambRef.current?.pause();
      stopMusic();
    } else {
      try {
        await el.play();
        if (ambRef.current) {
          ambRef.current.volume = Math.max(0, Math.min(1, ambienceVolume));
          ambRef.current.currentTime = 0;
          ambRef.current.play().catch(() => {});
        }
        if (musicSrc) startMusic(el.currentTime || 0);
      } catch (_) { /* ignore */ }
    }
  };

  const fmt = (s) => {
    const m = Math.floor((s || 0) / 60);
    const sec = Math.floor((s || 0) % 60);
    return `${m}:${String(sec).padStart(2, "0")}`;
  };

  const fmtShort = (s) => {
    const m = Math.floor((s || 0) / 60);
    return `${m}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
  };

  return (
    <div className="space-y-2" data-testid="studio-timeline">
      <audio
        ref={audioRef}
        src={url || undefined}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); ambRef.current?.pause(); stopMusic(); }}
        onTimeUpdate={(e) => setTime(e.target.currentTime)}
        onLoadedMetadata={(e) => {
          if (Number.isFinite(e.target.duration) && e.target.duration > 0) setDuration(e.target.duration);
        }}
      />
      {ambUrl && <audio ref={ambRef} src={ambUrl} loop/>}
      {musicSrc && <audio ref={musicRef} src={musicSrc} loop crossOrigin="anonymous"/>}
      {chapters.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pl-[52px]" data-testid="studio-timeline-chapters">
          {chapters.map((c, i) => {
            const active = duration && time >= c.start_seconds &&
              (i + 1 >= chapters.length || time < chapters[i + 1].start_seconds);
            return (
              <button
                key={i}
                type="button"
                onClick={() => seekTo((c.start_seconds || 0) / (duration || 1))}
                data-active={active ? "true" : "false"}
                data-testid={`studio-chapter-jump-${i}`}
                className={`px-2 py-0.5 rounded-full text-[10.5px] font-medium transition-colors ${
                  active
                    ? "bg-[var(--rammy-cyan)] text-[var(--rammy-text)]"
                    : "bg-white/10 text-white/70 hover:bg-white/20"
                }`}
                title={`Jump to ${c.title}`}
              >
                {fmtShort(c.start_seconds)} · {c.title}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggle}
          className="w-10 h-10 rounded-full bg-[var(--rammy-cyan)] text-[var(--rammy-text)] flex items-center justify-center shrink-0 hover:scale-105 active:scale-95 transition-transform"
          data-testid="studio-timeline-toggle"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <L.Pause size={16}/> : <L.Play size={16}/>}
        </button>
        <button
          type="button"
          onClick={() => seekTo(Math.max(0, (time - 5) / (duration || 1)))}
          className="text-white/60 hover:text-white transition-colors"
          data-testid="studio-timeline-back"
          aria-label="Back 5 seconds"
        >
          <L.Rewind size={15}/>
        </button>
        <canvas
          ref={canvasRef}
          onClick={onCanvasClick}
          className="flex-1 h-12 cursor-pointer rounded-lg"
          data-testid="studio-timeline-canvas"
        />
        <div className="text-[11px] font-mono text-white/70 w-[86px] text-right shrink-0" data-testid="studio-timeline-time">
          {fmt(time)} / {fmt(duration)}
        </div>
      </div>
    </div>
  );
}
